import { TeChip, TeContainer, TeContainerSlim, TeText, TeTooltip } from '@topcon/tuicl-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppService } from '../providers/app/app.context';
import { groupByFocusIndustry } from '../utils/utils';
import { ProfileBody } from '../api/generated/pap';
import { resolveProfileDescription, resolveProfileDisplayName } from '../utils/profiles';

interface Props {
  profiles: string[];
  setProfiles: (selectedProfiles: string[]) => void;
}

const ProfileSelector = ({ profiles, setProfiles }: Props) => {
  const { t } = useTranslation(['profile']);
  const { availableProfiles } = useAppService();
  const groupedProfiles: Record<string, ProfileBody[]> = groupByFocusIndustry(availableProfiles);

  const setActiveProfiles = (profileId: string, desiredState?: boolean) => {
    const isSelected = profiles.includes(profileId);
    const shouldBeSelected = typeof desiredState === 'boolean' ? desiredState : !isSelected;
    if (shouldBeSelected && !isSelected) {
      setProfiles([...profiles, profileId]);
    } else if (!shouldBeSelected && isSelected) {
      setProfiles(profiles.filter((value) => value !== profileId));
    }
  };

  return (
    <>
      {Object.keys(groupedProfiles).map((focusIndustry) => {
        return (
          <TeContainer
            column
            fill
            gap="s"
            padding="s"
            backgroundColor="background.tertiary"
            borderRadius={4}
            key={focusIndustry}
          >
            <TeContainerSlim column gap="xs3">
              <TeText size={12} weight={700}>
                {t(`${focusIndustry}.details.title`, { ns: 'profile' }) ?? focusIndustry}
              </TeText>
              <TeText size={12} italic color="text.secondary">
                {t(`${focusIndustry}.details.description`, { ns: 'profile' }) ?? focusIndustry}
              </TeText>
            </TeContainerSlim>
            <TeContainerSlim gap="xs2" alignItems="center" grow wrap>
              {groupedProfiles[focusIndustry].map((item) => {
                const { profileId } = item;
                const popkey = `profile-${profileId}`;
                return (
                  <Fragment key={profileId}>
                    <TeChip
                      popkey={`${profileId}-${popkey}`}
                      text={resolveProfileDisplayName(item, t)}
                      toggled={profiles.includes(profileId)}
                      tetoggle={(e) => setActiveProfiles(profileId, e.toggled)}
                    />
                    <TeTooltip
                      for={`${profileId}-${popkey}`}
                      text={resolveProfileDescription(item, t)}
                      zIndex="overlay.top"
                    />
                  </Fragment>
                );
              })}
            </TeContainerSlim>
          </TeContainer>
        );
      })}
    </>
  );
};

export default ProfileSelector;
